"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Card, Typography, Button, Grid, Box, IconButton, Skeleton, CircularProgress, Chip, Avatar } from "@mui/material"
import { motion } from "framer-motion"
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonButton, IonIcon, useIonViewWillEnter } from "@ionic/react"
import { refreshOutline } from "ionicons/icons"
import {
  People,
  Work,
  AttachMoney,
  Assignment,
  Group,
  Assessment,
  AccountBalance,
  Receipt,
  Dashboard,
  Analytics,
  Timeline,
  Notifications,
  Settings,
  TrendingUp,
  TrendingDown,
  Event,
  Schedule,
} from "@mui/icons-material"
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
} from "recharts"
import axios from "axios"
import { requestID, baseUrl, config } from "../../common/utils"
import { formatCurrencyN } from "../../common/utils_t"

// Sample data for HR dashboard
const employeeData = [
  { month: "Jan", hires: 12, terminations: 3, headcount: 145 },
  { month: "Feb", hires: 8, terminations: 2, headcount: 151 },
  { month: "Mar", hires: 15, terminations: 5, headcount: 161 },
  { month: "Apr", hires: 10, terminations: 4, headcount: 167 },
  { month: "May", hires: 18, terminations: 3, headcount: 182 },
  { month: "Jun", hires: 14, terminations: 6, headcount: 190 },
]

const departmentData = [
  { name: "Engineering", value: 35, color: "#8884d8" },
  { name: "Sales", value: 25, color: "#82ca9d" },
  { name: "Marketing", value: 15, color: "#ffc658" },
  { name: "Operations", value: 12, color: "#ff7300" },
  { name: "HR", value: 8, color: "#00ff88" },
  { name: "Finance", value: 5, color: "#0088ff" },
]

const leaveData = [
  { type: "Annual Leave", used: 65, allocated: 100 },
  { type: "Sick Leave", used: 45, allocated: 80 },
  { type: "Maternity", used: 12, allocated: 15 },
  { type: "Paternity", used: 8, allocated: 10 },
  { type: "Unpaid", used: 25, allocated: 0 },
]

const sampleUpcomingEvents = [
  { id: 1, type: "Training", title: "Leadership Workshop", date: "Tomorrow, 10:00 AM", participants: 15 },
  { id: 2, type: "Meeting", title: "Quarterly Review", date: "Jul 15, 2:00 PM", participants: 8 },
  { id: 3, type: "Event", title: "Company Picnic", date: "Jul 20, 12:00 PM", participants: "All" },
  { id: 4, type: "Deadline", title: "Performance Reviews Due", date: "Jul 25, 5:00 PM", participants: "Managers" },
]

const sampleRecentActivities = [
  { id: 1, employee: "Sarah Johnson", action: "Promotion", role: "Senior Developer", time: "2 hours ago" },
  { id: 2, employee: "Michael Chen", action: "Hired", role: "Marketing Specialist", time: "Yesterday" },
  { id: 3, employee: "Emma Wilson", action: "Leave Request", type: "Sick Leave (3 days)", time: "Yesterday" },
  { id: 4, employee: "David Brown", action: "Salary Adjustment", change: "+8%", time: "2 days ago" },
]

interface UserData {
  firstName: string
  middleName: string
  lastName: string
}

const HraasPage: React.FC = () => {
  const [userData, setUserData] = useState<UserData | null>({ firstName: "HR", middleName: "", lastName: "Manager" })
  const [loading, setLoading] = useState(false)
  const [lastRefresh, setLastRefresh] = useState(new Date())
  const [selectedTimeframe, setSelectedTimeframe] = useState("7d")
  const [loaddata, setLoadData] = useState(false)

  // HR-specific state
  const [totalEmployees, setTotalEmployees] = useState(0)
  const [activeRecruitments, setActiveRecruitments] = useState(0)
  const [pendingRequests, setPendingRequests] = useState(0)
  const [upcomingEventsCount, setUpcomingEventsCount] = useState(0)
  const [recentActivities, setRecentActivities] = useState<any[]>([])
  const [status, setStatus] = useState({
    loading: false,
    error: false,
    message: "",
  })

  useEffect(() => {
    // Simulate loading user data
    const storedData = localStorage.getItem("userData")
    if (storedData) {
      setUserData(JSON.parse(storedData))
    }
  }, [])

  const handleRefresh = () => {
    setLoading(true)
    setTimeout(() => {
      setLastRefresh(new Date())
      setLoading(false)
    }, 1000)
  }

  const quickMenuItems = [
    { icon: <People />, label: "Employees", color: "#1976d2", count: null },
    { icon: <Work />, label: "Recruitment", color: "#388e3c", count: "5" },
    { icon: <Assignment />, label: "Performance", color: "#f57c00", count: "24" },
    { icon: <Event />, label: "Time Off", color: "#7b1fa2", count: "12" },
    { icon: <AttachMoney />, label: "Payroll", color: "#d32f2f", count: null },
    { icon: <Group />, label: "Teams", color: "#0288d1", count: null },
    { icon: <Notifications />, label: "Requests", color: "#e91e63", count: "8" },
    { icon: <Settings />, label: "Settings", color: "#795548", count: null },
  ]

  const timeframeOptions = [
    { value: "24h", label: "24 Hours" },
    { value: "7d", label: "7 Days" },
    { value: "30d", label: "30 Days" },
    { value: "90d", label: "90 Days" },
  ]

  // Make the function async
  const getHrDashboard = async () => {
    setStatus({ loading: true, error: false, message: "Loading HR dashboard..." })

    try {
      setLoadData(false)
      const formData = new FormData()
      formData.append("requestID", requestID())

      const response = await axios.post(
        baseUrl() + "hr/getDashboardInfo",
        formData,
        config
      )
      
      console.log("HR dashboard response", response)
      const data = response.data

      setTotalEmployees(data.total_employees || 0)
      setActiveRecruitments(data.active_recruitments || 0)
      setPendingRequests(data.pending_requests || 0)
      setUpcomingEventsCount(data.upcoming_events || 0)
      setRecentActivities(data.recent_activities || [])
      setLoadData(true)
      setStatus({ loading: false, error: false, message: "Dashboard loaded" })
    } catch (error) {
      console.error("Error fetching HR dashboard:", error)
      // Use sample data if API fails
      setTotalEmployees(190)
      setActiveRecruitments(5)
      setPendingRequests(12)
      setUpcomingEventsCount(4)
      setRecentActivities(sampleRecentActivities)
      setLoadData(true)
      setStatus({ loading: false, error: true, message: "Using sample data - API failed" })
    }
  }

  useIonViewWillEnter(() => {
    getHrDashboard()
  }, [])

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar color="primary">
          <IonTitle>👥 HR Dashboard - Welcome, {userData ? `${userData.firstName}` : "HR Manager"}!</IonTitle>
          <IonButtons slot="end">
            <Typography variant="caption" style={{ marginRight: "10px", color: "white" }}>
              Last updated: {lastRefresh.toLocaleTimeString()}
            </Typography>
            <IonButton fill="clear" onClick={handleRefresh} disabled={loading}>
              <IonIcon
                icon={refreshOutline}
                style={{
                  animation: loading ? "spin 1s linear infinite" : "none",
                  color: "white",
                }}
              />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <Box
          sx={{
            padding: "20px",
            maxWidth: "1400px",
            margin: "0 auto",
            paddingBottom: "40px",
          }}
        >
          {/* Quick Menu Section */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: "#1976d2" }}>
              🚀 Quick Actions
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {quickMenuItems.map((item, index) => (
                <Grid item xs={6} sm={4} md={3} lg={1.5} key={index}>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Card
                      sx={{
                        p: 2,
                        textAlign: "center",
                        cursor: "pointer",
                        backgroundColor: item.color,
                        color: "white",
                        position: "relative",
                        "&:hover": { opacity: 0.9 },
                        minHeight: "80px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                      }}
                    >
                      {item.count && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            backgroundColor: "#ff4444",
                            color: "white",
                            borderRadius: "50%",
                            width: 24,
                            height: 24,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                            fontWeight: "bold",
                          }}
                        >
                          {item.count}
                        </Box>
                      )}
                      {item.icon}
                      <Typography variant="caption" display="block" sx={{ mt: 1, fontSize: "11px" }}>
                        {item.label}
                      </Typography>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>

          {/* HR Metrics Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: "#1976d2" }}>
              📊 HR Metrics
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, backgroundColor: "#e3f2fd", borderLeft: "5px solid #1976d2" }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      {loaddata ? (
                        <Typography variant="h4" fontWeight="bold" color="#1976d2">
                          {totalEmployees}
                        </Typography>
                      ) : (
                        <CircularProgress size={28} thickness={5} color="primary" />
                      )}
                      <Typography variant="body2" color="textSecondary">
                        Total Employees
                      </Typography>
                    </Box>
                    <People sx={{ fontSize: 50, color: "#1976d2", opacity: 0.7 }} />
                  </Box>
                  <Box display="flex" alignItems="center" mt={2}>
                    <TrendingUp sx={{ color: "green", mr: 1 }} />
                    <Typography variant="caption" color="green" fontWeight="bold">
                      +5% from last quarter
                    </Typography>
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, backgroundColor: "#e8f5e8", borderLeft: "5px solid #388e3c" }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      {loaddata ? (
                        <Typography variant="h4" fontWeight="bold" color="#388e3c">
                          {activeRecruitments}
                        </Typography>
                      ) : (
                        <CircularProgress size={28} thickness={5} color="success" />
                      )}
                      <Typography variant="body2" color="textSecondary">
                        Active Recruitments
                      </Typography>
                    </Box>
                    <Work sx={{ fontSize: 50, color: "#388e3c", opacity: 0.7 }} />
                  </Box>
                  <Box display="flex" alignItems="center" mt={2}>
                    <TrendingDown sx={{ color: "red", mr: 1 }} />
                    <Typography variant="caption" color="red" fontWeight="bold">
                      -2 from last month
                    </Typography>
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, backgroundColor: "#fff3e0", borderLeft: "5px solid #f57c00" }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      {loaddata ? (
                        <Typography variant="h4" fontWeight="bold" color="#f57c00">
                          {pendingRequests}
                        </Typography>
                      ) : (
                        <CircularProgress size={28} thickness={5} color="warning" />
                      )}
                      <Typography variant="body2" color="textSecondary">
                        Pending Requests
                      </Typography>
                    </Box>
                    <Notifications sx={{ fontSize: 50, color: "#f57c00", opacity: 0.7 }} />
                  </Box>
                  <Box display="flex" alignItems="center" mt={2}>
                    <TrendingUp sx={{ color: "green", mr: 1 }} />
                    <Typography variant="caption" color="green" fontWeight="bold">
                      +3 from yesterday
                    </Typography>
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, backgroundColor: "#f3e5f5", borderLeft: "5px solid #7b1fa2" }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      {loaddata ? (
                        <Typography variant="h4" fontWeight="bold" color="#7b1fa2">
                          {upcomingEventsCount}
                        </Typography>
                      ) : (
                        <CircularProgress size={28} thickness={5} color="secondary" />
                      )}
                      <Typography variant="body2" color="textSecondary">
                        Upcoming Events
                      </Typography>
                    </Box>
                    <Event sx={{ fontSize: 50, color: "#7b1fa2", opacity: 0.7 }} />
                  </Box>
                  <Box display="flex" alignItems="center" mt={2}>
                    <TrendingUp sx={{ color: "green", mr: 1 }} />
                    <Typography variant="caption" color="green" fontWeight="bold">
                      +2 this week
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </motion.div>

          {/* Charts Section */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Employee Trend Chart */}
            <Grid item xs={12} lg={8}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Card sx={{ p: 3, backgroundColor: "white", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h6" fontWeight="bold" color="#1976d2">
                      📈 Employee Headcount Trend
                    </Typography>
                    <IconButton onClick={handleRefresh} size="small">
                      <IonIcon icon={refreshOutline} />
                    </IconButton>
                  </Box>
                  <ResponsiveContainer width="100%" height={350}>
                    <AreaChart data={employeeData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                        }}
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="headcount"
                        stackId="1"
                        stroke="#8884d8"
                        fill="#8884d8"
                        fillOpacity={0.6}
                      />
                      <Area
                        type="monotone"
                        dataKey="hires"
                        stackId="1"
                        stroke="#82ca9d"
                        fill="#82ca9d"
                        fillOpacity={0.6}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Card>
              </motion.div>
            </Grid>

            {/* Department Distribution */}
            <Grid item xs={12} lg={4}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <Card sx={{ p: 3, backgroundColor: "white", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                  <Typography variant="h6" fontWeight="bold" color="#1976d2" mb={3}>
                    🥧 Department Distribution
                  </Typography>
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie
                        data={departmentData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {departmentData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              </motion.div>
            </Grid>

            {/* Leave Utilization */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <Card sx={{ p: 3, backgroundColor: "white", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                  <Typography variant="h6" fontWeight="bold" color="#1976d2" mb={3}>
                    📊 Leave Utilization
                  </Typography>
                  <ResponsiveContainer width="极狐" height={300}>
                    <BarChart data={leaveData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="type" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                        }}
                      />
                      <Legend />
                      <Bar dataKey="used" fill="#8884d8" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="allocated" fill="#82ca9d" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </motion.div>
            </Grid>

            {/* Upcoming Events */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
              >
                <Card sx={{ p: 3, backgroundColor: "极狐", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                  <Typography variant="h6" fontWeight="bold" color="#1976d2" mb={3}>
                    📅 Upcoming Events
                  </Typography>
                  <Box>
                    {sampleUpcomingEvents.map((event, index) => (
                      <motion.div
                        key={event.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        whileHover={{ backgroundColor: "#f8f9fa" }}
                        style={{ padding: "16px", borderRadius: "12px", marginBottom: "12px", border: "1px solid #eee" }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box display="flex" alignItems="center">
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: "50%",
                                backgroundColor: 
                                  event.type === "Training" ? "#e8f5e8" : 
                                  event.type === "Meeting" ? "#e3f2fd" : 
                                  event.type === "Event" ? "#f3e5f5" : "#fff3e0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mr: 3,
                              }}
                            >
                              {event.type === "Training" ? (
                                <Group sx={{ color: "#388e3c", fontSize: 24 }} />
                              ) : event.type === "Meeting" ? (
                                <People sx={{ color: "#1976d2", fontSize: 24 }} />
                              ) : event.type === "Event" ? (
                                <Event sx={{ color: "#7b1fa2", fontSize: 24 }} />
                              ) : (
                                <Schedule sx={{ color: "#f57c00", fontSize: 24 }} />
                              )}
                            </Box>
                            <Box>
                              <Typography variant="body1" fontWeight="bold">
                                {event.title}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {event.date} • {event.participants} participants
                              </Typography>
                            </Box>
                          </Box>
                          <Chip 
                            label={event.type} 
                            size="small" 
                            color={
                              event.type === "Training" ? "success" : 
                              event.type === "Meeting" ? "primary" : 
                              event.type === "Event" ? "secondary" : "warning"
                            } 
                          />
                        </Box>
                      </motion.div>
                    ))}
                  </Box>
                </Card>
              </motion.div>
            </Grid>
          </Grid>

          {/* Recent HR Activities */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <Card sx={{ p: 3, backgroundColor: "white", boxShadow: "0 极狐 20px rgba(0,0,0,0.1)" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight="bold" color="#1976d2">
                  🔔 Recent HR Activities
                </Typography>
                <Button variant="outlined" size="small" startIcon={<Timeline />}>
                  View All Activities
                </Button>
              </Box>
              <Box>
                {recentActivities.map((activity, index) => (
                  <motion.div
                    key={activity.id || index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    whileHover={{ backgroundColor: "#f8f9fa" }}
                    style={{ padding: "16px", borderRadius: "12px", marginBottom: "12px", border: "1px solid #eee" }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" alignItems="center">
                        <Avatar sx={{ width: 40, height: 40, mr: 2, bgcolor: getRandomColor() }}>
                          {activity.employee ? activity.employee.charAt(0) : "U"}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {activity.employee || "Unknown Employee"}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {activity.action} {activity.role && `to ${activity.role}`} {activity.type && `- ${activity.type}`}
                          </Typography>
                        </Box>
                      </Box>
                      <Box textAlign="right">
                        <Typography variant="caption" color="textSecondary">
                          {activity.time || "Recently"}
                        </Typography>
                        {activity.change && (
                          <Chip 
                            label={activity.change} 
                            size="small" 
                            color={activity.change.includes('+') ? "success" : "error"}
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    </Box>
                  </motion.div>
                ))}
              </Box>
            </Card>
          </motion.div>
        </Box>

        <style jsx>{`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </IonContent>
    </IonPage>
  )
}

// Helper function to generate random colors for avatars
function getRandomColor() {
  const colors = [
    '#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5',
    '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50',
    '#8bc34a', '#cddc39', '#ffeb3b', '#ffc107', '#ff9800',
    '#ff5722', '#795548', '#607d8b'
  ];
  return colors[Math.floor(Math.random() * colors.length)];
}

export default HraasPage